/**
 * Utility functions for managing extra parameters in dataset records
 */

/** Matches `origin_labels`, `origin_labels1`, `origin_labels2`, … */
export const ORIGIN_LABELS_COLUMN_RE = /^origin_labels(\d*)$/;

/**
 * User-facing name for origin-label extra-param columns.
 * `origin_labels` → "Original Labels"; `origin_labels1` → "Original Labels1".
 * Other keys are returned unchanged.
 */
export function formatOriginLabelsColumnLabel(key) {
    if (typeof key !== 'string') return key;
    const match = key.match(ORIGIN_LABELS_COLUMN_RE);
    if (!match) return key;
    return `Original Labels${match[1]}`;
}

const HEADER_FONT_PX = 12;
// Spark header cells are 12px medium uppercase. A 0.7em factor under-measures
// that face, so "Original Labels" + filter icon clips and longer titles hide
// the icon entirely. Use ~1em per glyph plus letter-spacing.
const HEADER_CHAR_FACTOR = 1.05;
const HEADER_LETTER_SPACING_PX = 0.5;
const HEADER_CELL_PADDING_PX = 32;
const FILTER_ICON_PX = 24;
const FILTER_GAP_PX = 4;
const WIDTH_BUFFER_PX = 16;
const MIN_EXTRA_PARAM_COLUMN_PX = 180;

/**
 * Pixel width that fits an extra-param header title (Spark headers are
 * uppercase, letter-spaced, border-box padded) plus an optional filter icon.
 */
export function extraParamColumnMinWidth(label, { hasFilter = false } = {}) {
    const text = String(label ?? '').toUpperCase();
    const textWidth = text.length * (HEADER_FONT_PX * HEADER_CHAR_FACTOR + HEADER_LETTER_SPACING_PX);
    const iconWidth = hasFilter ? FILTER_ICON_PX + FILTER_GAP_PX : 0;
    return Math.ceil(Math.max(
        MIN_EXTRA_PARAM_COLUMN_PX,
        textWidth + HEADER_CELL_PADDING_PX + iconWidth + WIDTH_BUFFER_PX,
    ));
}

function isMissingExtraParamValue(value) {
    return (
        value === undefined
        || value === null
        || value === ''
        || (Array.isArray(value) && value.length === 0)
    );
}

function parseExtraParamsDict(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
    return null;
}

function extraParamsDict(source) {
    return parseExtraParamsDict(source?.extra_params);
}

function extraParamSources(record) {
    const sources = [];
    const push = (source) => {
        if (source && typeof source === 'object' && !Array.isArray(source)) {
            sources.push(source);
        }
    };
    push(record);
    push(record?.response);
    push(record?.origin_response);
    const responses = record?.responses;
    if (responses && typeof responses === 'object' && !Array.isArray(responses)) {
        Object.values(responses).forEach(push);
    }
    return sources;
}

function lookupExtraParam(dict, key) {
    if (!dict || key == null) return undefined;
    if (dict[key] !== undefined) return dict[key];
    const prefixed = `extra_param_${key}`;
    if (dict[prefixed] !== undefined) return dict[prefixed];
    const lower = String(key).toLowerCase();
    const match = Object.keys(dict).find((k) => k.toLowerCase() === lower);
    return match !== undefined ? dict[match] : undefined;
}

/**
 * Resolve an extra-param value from nested, flattened, or JSON-string record shapes.
 * Empty arrays/strings are skipped so they do not shadow a populated copy.
 */
export function getExtraParamRaw(record, key, fallback) {
    const candidates = [];
    extraParamSources(record).forEach((source) => {
        candidates.push(lookupExtraParam(extraParamsDict(source), key));
        candidates.push(source?.[key]);
        candidates.push(source?.[`extra_param_${key}`]);
    });
    candidates.push(record?.[`extra_param_${key}`]);
    candidates.push(fallback);
    if (key === 'origin_labels') {
        candidates.push(record?.origin_response?.labels);
    }
    for (const value of candidates) {
        if (!isMissingExtraParamValue(value)) return value;
    }
    return undefined;
}

/**
 * Extract all unique extra parameter keys from a dataset
 * @param {Array} dataset - Array of dataset records
 * @returns {Array} Array of unique extra parameter keys
 */
export function extractAllExtraParamsKeys(dataset) {
    if (!dataset || dataset.length === 0) return [];

    const allKeys = new Set();
    dataset.forEach(record => {
        if (record.response?.extra_params) {
            Object.keys(record.response.extra_params).forEach(key => {
                allKeys.add(key);
            });
        }
    });

    return Array.from(allKeys).sort();
}

/**
 * Get the value of an extra parameter from a record
 * @param {Object} record - Dataset record
 * @param {string} key - Extra parameter key
 * @returns {*} The value of the extra parameter
 */
export function getExtraParamValue(record, key) {
    const raw = getExtraParamRaw(record, key);
    if (raw === undefined) return '';
    return typeof raw === 'object' ? JSON.stringify(raw) : raw;
}

/**
 * Resolve a raw extra-param value as a normalized array of entity objects.
 * Accepts either an already-parsed array or a JSON string (as ingested from CSV
 * uploads). Returns the array only when every item is entity-shaped
 * (`content`, `label`, numeric `start_index`); otherwise returns null so the
 * caller can fall back to the plain string display.
 * @param {Object} record - Dataset record
 * @param {string} key - Extra parameter key (defaults to 'entities')
 * @returns {Array|null} Array of entity objects, or null if not entity-shaped
 */
export function getExtraParamEntities(record, key = 'entities') {
    const raw = getExtraParamRaw(record, key);
    if (raw === undefined || raw === null) return null;

    let parsed = raw;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return null;
        }
    }

    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    const isEntityShaped = parsed.every(
        (item) =>
            item &&
            typeof item === 'object' &&
            typeof item.content === 'string' &&
            typeof item.label === 'string' &&
            typeof item.start_index === 'number'
    );

    return isEntityShaped ? parsed : null;
}

/**
 * Tokenize a cross-referenced classification labels value into an array of label
 * strings, using the sibling sub-task's upload configuration (single- vs
 * multi-label and the chosen delimiter) rather than guessing.
 *
 * Mirrors the backend `tokenize_multilabel_value`
 * (optune-utils/.../utils/dataset_utils.py) so tags match the owning sub-task:
 * - single-label: the whole value is one label
 * - multi-label + delimiter 'list' (or an array value): parse/iterate the list
 * - multi-label + other delimiter: split by that delimiter
 *
 * @param {*} value - Raw extra-param value (string, array, or JSON-array string)
 * @param {string|null} delimiter - Configured delimiter, or 'list', or null
 * @param {boolean} isMultilabel - Whether the sibling task is multi-label
 * @returns {Array<string>|null} Array of labels, or null when empty/unusable
 */
function labelToken(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value).trim();
    }
    if (Array.isArray(value) || typeof value !== 'object') return '';
    const inner = value.label ?? value.name ?? value.value ?? value.text;
    if (inner == null || inner === value) return '';
    return labelToken(inner);
}

export function tokenizeLabels(value, delimiter, isMultilabel) {
    if (value === undefined || value === null) return null;

    const clean = (arr) => arr.map(labelToken).filter(Boolean);

    if (Array.isArray(value)) {
        const out = clean(value);
        return out.length ? out : null;
    }

    if (typeof value === 'object') {
        const fromObject = labelToken(value);
        if (fromObject) return [fromObject];
        return null;
    }

    const s = String(value).trim();
    if (!s) return null;

    if (s.startsWith('[')) {
        try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) {
                const out = clean(parsed);
                return out.length ? out : null;
            }
        } catch {
            // not a JSON array; keep going
        }
    }

    if (!isMultilabel) return [s];

    if (delimiter === 'list') {
        return [s];
    }

    if (delimiter) {
        const out = clean(s.split(delimiter));
        return out.length ? out : null;
    }

    return [s];
}

/**
 * Aggregate items into per-type counts.
 * @param {Array} items - Items to count
 * @param {Function} getType - Maps an item to its type/label string
 * @returns {Object} Map of type -> count
 */
export function countByLabel(items, getType) {
    return (items || []).reduce((acc, item) => {
        const label = getType(item) || 'UNKNOWN';
        acc[label] = (acc[label] || 0) + 1;
        return acc;
    }, {});
}

/**
 * Check if a record has a specific extra parameter
 * @param {Object} record - Dataset record
 * @param {string} key - Extra parameter key
 * @returns {boolean} True if the record has the extra parameter
 */
export function hasExtraParam(record, key) {
    return record.response?.extra_params?.[key] !== undefined || record[key] !== undefined;
}

/**
 * Get all extra parameter values for a record
 * @param {Object} record - Dataset record
 * @returns {Object} Object containing all extra parameter key-value pairs
 */
export function getAllExtraParams(record) {
    return record.response?.extra_params || {};
}

/**
 * Validate if extra parameter keys are valid for the dataset
 * @param {Array} keys - Array of extra parameter keys
 * @param {Array} dataset - Array of dataset records
 * @returns {Array} Array of valid keys that exist in the dataset
 */
export function validateExtraParamKeys(keys, dataset) {
    const availableKeys = extractAllExtraParamsKeys(dataset);
    return keys.filter(key => availableKeys.includes(key));
}


// Function to extract extra parameters from prompt (excluding {{example}})
// Examples:
// - "Classify {{sentiment}} for {{text}}" -> {sentiment: '', text: ''}
// - "Analyze {{example}} with {{context}}" -> {context: ''} (example is excluded)
// - "Process {{data}} using {{method}} and {{example}}" -> {data: '', method: ''}
export const extractExtraParamsFromPrompt = (promptText) => {
    if (!promptText) return {};

    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [...promptText.matchAll(regex)];
    const extraParams = {};

    matches.forEach(match => {
        const paramName = match[1].trim();
        if (paramName !== 'example') {
            extraParams[paramName] = '';
        }
    });

    return extraParams;
};